import { twMerge } from "tailwind-merge";

import {
  Button,
  DeleteIcon,
  EditIcon,
  Table,
  TableData,
  TableHead,
  TableHeader,
} from "~/common/components";
import { formatDateWithHour } from "~/common/utils";
import { MAXIMUM_QUESTIONS } from "~/providers/constants/supplementalQuestions";
import { SupplementalQuestion } from "~/providers/types/supplementalQuestions";
import { useSupplementalQuestionsEditorContext } from "./SupplementalQuestionsEditor";

interface SupplementalQuestionsTableProps {
  onReorder: () => void;
  onAddQuestion: () => void;
  onEditQuestion: (question: SupplementalQuestion) => void;
  onDeleteQuestion: (question: SupplementalQuestion) => void;
}

export const SupplementalQuestionsTable = ({
  onReorder,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
}: SupplementalQuestionsTableProps) => {
  const { type, questions } = useSupplementalQuestionsEditorContext();

  const canOrder = questions.length > 1;
  const canAddQuestion = questions.length < MAXIMUM_QUESTIONS;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h3 className="ml-0 mr-auto text-lg capitalize text-gray-70">{type}</h3>
        <Button
          variant="outline"
          className={twMerge("w-fit", !canOrder && "cursor-not-allowed")}
          onClick={onReorder}
          disabled={!canOrder}
        >
          Edit order
        </Button>
        <Button
          className="w-fit"
          onClick={onAddQuestion}
          disabled={!canAddQuestion}
        >
          Add question
        </Button>
      </div>
      <div className="w-full overflow-x-auto rounded-xl border border-gray-200">
        <Table className="size-full table-auto">
          <TableHead className="rounded-t-xl">
            <tr>
              <TableHeader>Question</TableHeader>
              <TableHeader className="w-1 sm:w-48">Updated</TableHeader>
              <TableHeader className="w-1 sm:w-20" />
            </tr>
          </TableHead>
          <tbody>
            {questions.map((q, index) => {
              return (
                <tr key={index}>
                  <TableData>{q.text}</TableData>
                  <TableData>{formatDateWithHour(q.updated_at)} </TableData>
                  <TableData>
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => onEditQuestion(q)}>
                        <EditIcon className="size-5" />
                      </button>
                      <button onClick={() => onDeleteQuestion(q)}>
                        <DeleteIcon className="size-5" />
                      </button>
                    </div>
                  </TableData>
                </tr>
              );
            })}

            {questions.length === 0 && (
              <tr>
                <TableData className="text-center" colSpan={3}>
                  No questions added.
                </TableData>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
